import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Package, Info } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { ProductSelector, PRODUCTS } from '@/components/onboarding/ProductSelector'

export default function ProductSelection() {
  const { selectedProducts, setSelectedProducts, setStep } = useOnboardingStore()

  // Ensure we're on step 4
  useEffect(() => {
    setStep(4)
  }, [setStep])

  const availableProducts = PRODUCTS.filter((p) => p.available)
  const selectedCount = selectedProducts.length

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <Package className="w-5 h-5 text-copper-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900">
            Choose your products
          </h1>
        </div>
        <p className="text-warm-600">
          Select the Personic AI products you'd like to use. You can always add more later.
        </p>
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 p-4 bg-midnight-50 rounded-xl flex items-start gap-3"
      >
        <Info className="w-5 h-5 text-midnight-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-midnight-700">
            <strong className="font-heading">Select at least one product</strong> to continue.
            Currently, <strong>Engage</strong> and <strong>Coach</strong> are available, with
            more products coming soon.
          </p>
        </div>
      </motion.div>

      {/* Product Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <ProductSelector
          selectedProducts={selectedProducts}
          onChange={setSelectedProducts}
        />
      </motion.div>

      {/* Selection Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-8"
      >
        {selectedCount > 0 ? (
          <div className="text-center">
            <p className="text-sm text-warm-600">
              You've selected{' '}
              <span className="font-heading font-semibold text-copper-600">
                {selectedCount} {selectedCount === 1 ? 'product' : 'products'}
              </span>
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {selectedProducts.map((productId) => {
                const product = PRODUCTS.find((p) => p.id === productId)
                if (!product) return null
                return (
                  <span
                    key={productId}
                    className="inline-flex items-center px-3 py-1 bg-copper-100 text-copper-700 rounded-full text-sm font-heading"
                  >
                    {product.name}
                  </span>
                )
              })}
            </div>
          </div>
        ) : (
          <p className="text-sm text-warm-500 text-center">
            Please select at least one product to continue
          </p>
        )}
      </motion.div>

      {/* Available Products Note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-6 pt-6 border-t border-warm-200"
      >
        <p className="text-xs text-warm-500 text-center">
          {availableProducts.length} products available now,{' '}
          {PRODUCTS.length - availableProducts.length} coming soon
        </p>
      </motion.div>
    </div>
  )
}
